/**
 * A flag for where a period is.
 *
 * Doug asked for an emoji next to the country or city he is travelling to. It
 * earns its place on a list of twenty periods: a flag is recognised before it
 * is read, which is the whole job of the left edge of a row.
 *
 * Matched on the label, because that is all a period has — the schema records
 * `kind` (home2, travel) and a free-text label, and never a country.
 * So this is a lookup over the places Doug actually goes, and it returns the
 * empty string for anything it does not know rather than a globe: a wrong flag
 * is worse than none, and a generic one is just noise in every row.
 *
 * Airport codes are here too, because a trip derived from an itinerary is
 * labelled with the destination's IATA code — see `tripsFrom`, which uses the
 * code deliberately rather than guessing a city name.
 */
const BY_LABEL: [RegExp, string][] = [
  // Home and the recurring one
  [/\bberlin\b|\bdeutschland\b|\bgermany\b/i, '🇩🇪'],
  [/\bireland\b|\birland\b|\bdublin\b|\bcork\b|\bknock\b|\bshannon\b|\bkerry\b/i, '🇮🇪'],
  [/\b(dub|ork|snn|noc|kir)\b/i, '🇮🇪'],
  [/\b(ber|txl|sxf)\b/i, '🇩🇪'],

  // Where the year actually goes
  [/\brome\b|\brom\b|\bitaly\b|\bitalien\b|\bmilan\b|\bvenice\b|\bnaples\b/i, '🇮🇹'],
  [/\b(fco|cia|mxp|lin|vce|nap)\b/i, '🇮🇹'],
  [/\bparis\b|\bfrance\b|\bfrankreich\b|\bmarseille\b|\blyon\b/i, '🇫🇷'],
  [/\b(cdg|ory|mrs|lys|nce)\b/i, '🇫🇷'],
  [/\blondon\b|\bengland\b|\bscotland\b|\buk\b|\bbritain\b/i, '🇬🇧'],
  [/\b(lhr|lgw|stn|ltn|lcy|edi|man)\b/i, '🇬🇧'],
  [/\bvilnius\b|\blithuania\b|\blitauen\b/i, '🇱🇹'],
  [/\b(vno|kun)\b/i, '🇱🇹'],
  [/\bamsterdam\b|\bnetherlands\b|\bholland\b/i, '🇳🇱'],
  [/\b(ams|rtm|ein)\b/i, '🇳🇱'],
  [/\bspain\b|\bmadrid\b|\bbarcelona\b|\bspanien\b|\bmallorca\b/i, '🇪🇸'],
  [/\b(mad|bcn|agp|pmi|vlc)\b/i, '🇪🇸'],
  [/\bportugal\b|\blisbon\b|\blissabon\b|\bporto\b/i, '🇵🇹'],
  [/\b(lis|opo|fao)\b/i, '🇵🇹'],
  [/\bpoland\b|\bwarsaw\b|\bkrakow\b|\bpolen\b/i, '🇵🇱'],
  [/\b(waw|krk|gdn)\b/i, '🇵🇱'],
  [/\bczech\b|\bprague\b|\bprag\b/i, '🇨🇿'],
  [/\b(prg)\b/i, '🇨🇿'],
  [/\baustria\b|\bvienna\b|\bwien\b|\bösterreich\b/i, '🇦🇹'],
  [/\b(vie|szg|inn)\b/i, '🇦🇹'],
  [/\bswitzerland\b|\bzurich\b|\bzürich\b|\bgeneva\b|\bbasel\b/i, '🇨🇭'],
  [/\b(zrh|gva|bsl|brn)\b/i, '🇨🇭'],
  [/\bdenmark\b|\bcopenhagen\b|\bkopenhagen\b/i, '🇩🇰'],
  [/\b(cph|bll)\b/i, '🇩🇰'],
  [/\bsweden\b|\bstockholm\b|\bgothenburg\b/i, '🇸🇪'],
  [/\b(arn|got|nyo)\b/i, '🇸🇪'],
  [/\bnorway\b|\boslo\b|\bbergen\b/i, '🇳🇴'],
  [/\b(osl|bgo|trd)\b/i, '🇳🇴'],
  [/\bgreece\b|\bathens\b|\bcrete\b|\bgriechenland\b/i, '🇬🇷'],
  [/\b(ath|her|skg)\b/i, '🇬🇷'],
  [/\bbelgium\b|\bbrussels\b|\bbrüssel\b|\bantwerp\b/i, '🇧🇪'],
  [/\b(bru|crl|anr)\b/i, '🇧🇪'],
  [/\bhungary\b|\bbudapest\b/i, '🇭🇺'],
  [/\b(bud)\b/i, '🇭🇺'],
  [/\bcroatia\b|\bzagreb\b|\bsplit\b|\bdubrovnik\b/i, '🇭🇷'],
  [/\b(zag|spu|dbv)\b/i, '🇭🇷'],
  [/\bturkey\b|\bistanbul\b|\btürkei\b/i, '🇹🇷'],
  [/\b(ist|saw|ayt)\b/i, '🇹🇷'],
  [/\bmorocco\b|\bmarrakech\b|\bmarokko\b/i, '🇲🇦'],
  [/\b(rak|cmn|tng)\b/i, '🇲🇦'],
  [/\busa\b|\bnew york\b|\bunited states\b|\bamerica\b/i, '🇺🇸'],
  [/\b(jfk|lga|ewr|lax|sfo|ord|bos)\b/i, '🇺🇸'],
  [/\bjapan\b|\btokyo\b|\btokio\b|\bkyoto\b/i, '🇯🇵'],
  [/\b(nrt|hnd|kix)\b/i, '🇯🇵'],
]

/** A flag for a label, or '' where nothing is known. */
export function emojiFor(label: string | null | undefined): string {
  if (!label) return ''
  for (const [re, flag] of BY_LABEL) if (re.test(label)) return flag
  return ''
}

/** The flag emoji for an ISO 3166-1 alpha-2 code, or '' if it is not two
 *  letters. A flag is two regional-indicator symbols, one per letter, so this
 *  needs no lookup table and works for any country Doug picks. */
export function flagFromIso(code: string | null | undefined): string {
  if (!code || !/^[a-z]{2}$/i.test(code)) return ''
  const cc = code.toUpperCase()
  return String.fromCodePoint(...[...cc].map(c => 0x1f1e6 + c.charCodeAt(0) - 65))
}

/** The countries the Year form offers, in the order Doug is likeliest to reach
 *  for. Codes are ISO 3166-1 alpha-2; the label carries its own flag so the
 *  dropdown reads as a list of flags. Not exhaustive on purpose — a wrong flag
 *  is worse than none, and the free-text label still guesses for the rest. */
export const COUNTRIES: [string, string][] = [
  ['IE', 'Ireland'], ['DE', 'Germany'], ['GB', 'United Kingdom'], ['FR', 'France'],
  ['IT', 'Italy'], ['ES', 'Spain'], ['PT', 'Portugal'], ['NL', 'Netherlands'],
  ['BE', 'Belgium'], ['CH', 'Switzerland'], ['AT', 'Austria'], ['PL', 'Poland'],
  ['CZ', 'Czechia'], ['HU', 'Hungary'], ['HR', 'Croatia'], ['GR', 'Greece'],
  ['DK', 'Denmark'], ['SE', 'Sweden'], ['NO', 'Norway'], ['LT', 'Lithuania'],
  ['TR', 'Turkey'], ['MA', 'Morocco'], ['US', 'United States'], ['MX', 'Mexico'],
  ['JP', 'Japan'],
]

/**
 * The flag for a period.
 *
 * A stated country wins over everything: it is the one signal that cannot be a
 * wrong guess. Failing that, the second home (its country is the profile's,
 * passed in) and Berlin are known from `place`, and the
 * rest is a trip whose label is a city, a country or an airport code. A visit —
 * Rachel staying in Berlin — is Berlin, not a journey, so it takes the home
 * flag like any other Berlin stretch.
 */
export function placeEmoji(
  p: { place: string; label: string | null; country?: string | null },
  secondCountry?: string | null,
): string {
  if (p.country) return flagFromIso(p.country)
  if (p.place === 'Home2' && secondCountry) return flagFromIso(secondCountry)
  // Home is Berlin's flag while every home region is Berlin; C2.2 reads the
  // home region's country when a person's home can be another.
  if (p.place === 'Home') return '🇩🇪'
  return emojiFor(p.label)
}
